import { logger } from '@gik/analytics/utils/logger';
import { useUser } from '@gik/api/users/user';
import { useUserStore } from '@gik/core/store/UserStore';
import { useHideIntercomDefaultLauncher } from '@gik/core/utils/Intercom';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import {
  ThankYouForm,
  type ThankYouFormProps,
  type ThankYouFormValues,
} from '@gik/messaging/thank-you/components/ThankYouForm/ThankYouForm';
import { useGiftByUuid, useGiftSendThankYouMessage } from '@gik/platform-management-api-react';
import { LoadingSpinner } from '@gik/ui-legacy/LoadingSpinner';
import { UI } from '@gik/ui-legacy/UIManager';
import { HttpStatusCode } from 'axios';
import React from 'react';

export type GiftThankYouFormWrapperProps = {
  buttonsPortal?: React.MutableRefObject<HTMLElement>;
  customerId: string;
  giftUuid: string;
  onSuccess?: () => void;
  onFail?: () => void;
} & ThankYouFormProps;

function GiftThankYouFormWrapperComp({
  className,
  buttonsPortal,
  sender,
  recipient,
  customerId,
  giftUuid,
  onSuccess,
  onFail,
  ...otherProps
}: React.PropsWithChildren<GiftThankYouFormWrapperProps>): React.ReactElement {
  const bem = useBemCN('thank-you-form-wrapper');
  useHideIntercomDefaultLauncher();

  const sendThankYouMessage = useGiftSendThankYouMessage();

  const [values, setValues] = React.useState<ThankYouFormValues>();

  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const { data: gift } = useGiftByUuid(giftUuid);

  const handleSubmit = React.useCallback(
    async (values: ThankYouFormValues) => {
      try {
        const response = await sendThankYouMessage.mutateAsync({
          userId,
          giftUuid,
          customerId,
          ...values,
        });
        if (response.status === HttpStatusCode.Ok) {
          UI.notifySuccess('Thank you sent');
          onSuccess?.();
        } else {
          const msg = 'Failed to send Thank you';
          logger.warn(msg, { giftUuid, customerId: customerId, ...values });
          UI.notifyError(msg);
          onFail?.();
        }
      } catch (err) {
        logger.error(err);
      }
    },
    [sendThankYouMessage, userId, giftUuid, customerId, onSuccess, onFail]
  );

  React.useEffect(() => {
    if (!user || !gift) return;

    let greeting = `Dear Supporter`;
    if (gift.isAnonymous !== true) greeting = `Dear ${recipient.firstName || recipient.fullName}`;

    setValues({
      greeting,
      message: `Thank you so much for your care and support during this time.`,
      signature: `- Love ${user.firstName}`,
      backgroundColor: 'aqua',
    });
  }, [user, gift, recipient]);

  if (!values) return <LoadingSpinner center />;

  return (
    <div {...bem(null, null, className)}>
      <ThankYouForm
        buttonsPortal={buttonsPortal}
        autoFocus
        initialValues={values}
        onSubmit={handleSubmit}
        sender={sender}
        recipient={recipient}
        {...otherProps}
      />
    </div>
  );
}

export const GiftThankYouFormWrapper = withComponentErrorBoundary(GiftThankYouFormWrapperComp);
